import React, { useState, useEffect } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { Box, Container } from '@mui/material'
import { Header, Footer, ChairpersonSidebar } from '@/components/layout'
import { useChairpersonContext } from '@/context'
import { ROUTES } from '@/constants'

const slickScrollbar = {
  scrollbarWidth: 'thin',
  scrollbarColor: 'var(--primary-color, #e96e48) transparent',
  '&::-webkit-scrollbar': {
    width: 6,
    height: 6,
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
    borderRadius: 0,
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 8,
    backgroundColor: 'primary.main',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: 'primary.dark',
    },
  },
}

export const ChairpersonLayout = () => {
  const navigate = useNavigate()
  const { chairperson } = useChairpersonContext()
  const [selectedAccountId, setSelectedAccountId] = useState(null)

  const accounts = chairperson?.accounts ?? []
  const accountIds = accounts.map(a => a.id)

  useEffect(() => {
    if (accounts.length === 0) return
    const hasValidSelection =
      selectedAccountId != null && accountIds.includes(selectedAccountId)
    if (!hasValidSelection) {
      setSelectedAccountId(accounts[0].id)
    }
  }, [chairperson?.accounts])

  const handleAccountChange = accountId => {
    setSelectedAccountId(accountId)
    navigate(ROUTES.CHAIRPERSON_DASHBOARD)
    // TODO: Load account-specific data when accounts are fetched from API
  }

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Header />
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          overflow: { xs: 'auto', md: 'hidden' },
        }}
      >
        <Box
          component='aside'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            py: { xs: 1.5, md: 3 },
            pl: { xs: 1.5, md: 3 },
            pr: 0,
            position: 'relative',
            width: { xs: '100%', md: 300, lg: 340 },
            minWidth: { xs: 0, md: 300, lg: 340 },
            maxWidth: { xs: '100%', md: 300, lg: 340 },
            flexShrink: 0,
            overflow: 'hidden',
            borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              overflowX: 'hidden',
              overflowY: { xs: 'visible', md: 'auto' },
              overscrollBehavior: 'contain',
              pr: { xs: 1.5, md: 1 },
              ...slickScrollbar,
            }}
          >
            <ChairpersonSidebar
              selectedAccountId={selectedAccountId}
              onAccountChange={handleAccountChange}
            />
          </Box>
        </Box>
        <Container
          component='main'
          maxWidth='xl'
          sx={{
            py: { xs: 0.5, md: 3 },
            flex: 1,
            minWidth: 0,
            minHeight: { xs: 'auto', md: 0 },
            overflowX: 'hidden',
            overflowY: { xs: 'visible', md: 'auto' },
            overscrollBehavior: 'contain',
            ...slickScrollbar,
          }}
        >
          <Outlet context={{ selectedAccountId }} />
        </Container>
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <Footer />
      </Box>
    </Box>
  )
}
